import React, { useMemo } from 'react'
import { View, Text, Platform } from 'react-native'
import { Dropdown } from 'react-native-element-dropdown'
import { ChevronDown } from 'lucide-react-native'
import { useTheme } from '../../../src/theme/useTheme'
import { useDropdownOverlayPosition } from '../../../src/shared/hooks/useDropdownOverlayPosition'
import { LEAGUE_STANDINGS_SCOPE_ITEMS } from '../leagueDetail.constants'
import type { LeagueStandingsScope } from '../../../src/leagueDetail/types/leagueDetail.types'
import {
  getLeagueStandingsScopePickerStyles,
  STANDINGS_SCOPE_DROPDOWN_WIDTH,
} from './LeagueStandingsScopePicker.styles'

const DROPDOWN_DATA = LEAGUE_STANDINGS_SCOPE_ITEMS.map((item) => ({
  label: item.label,
  value: item.key,
}))

export interface LeagueStandingsScopePickerProps {
  value: LeagueStandingsScope
  onChange: (scope: LeagueStandingsScope) => void
  /** When false, only "Overall" exists in data — show a static pill. */
  venueSplitAvailable: boolean
}

export const LeagueStandingsScopePicker: React.FC<LeagueStandingsScopePickerProps> = ({
  value,
  onChange,
  venueSplitAvailable,
}) => {
  const theme = useTheme()
  const styles = useMemo(() => getLeagueStandingsScopePickerStyles(theme), [theme])

  const { triggerRef, overlayOffset, webOverlayStyle, measure } = useDropdownOverlayPosition({
    overlayWidth: STANDINGS_SCOPE_DROPDOWN_WIDTH,
    alignment: 'right',
  })

  const flatListProps = useMemo(
    () => ({
      nestedScrollEnabled: true,
      ...(Platform.OS === 'web' ? { style: styles.dropdownFlatListWeb } : {}),
    }),
    [styles.dropdownFlatListWeb],
  )

  if (!venueSplitAvailable) {
    return (
      <View style={styles.staticPill} accessibilityLabel="Table scope: Overall">
        <Text style={styles.staticPillText}>Overall</Text>
      </View>
    )
  }

  return (
    <View ref={triggerRef} style={styles.triggerWrap}>
      <Dropdown
        style={styles.dropdown}
        backgroundColor={theme.background.overlay}
        containerStyle={[
          styles.dropdownOverlayContainer,
          overlayOffset ?? undefined,
          webOverlayStyle as React.ComponentProps<typeof Dropdown>['containerStyle'],
        ]}
        placeholderStyle={styles.placeholderStyle}
        selectedTextStyle={styles.selectedTextStyle}
        itemContainerStyle={styles.dropdownItemContainer}
        itemTextStyle={styles.dropdownItemText}
        activeColor={theme.primary + '22'}
        dropdownPosition="auto"
        flatListProps={flatListProps}
        data={DROPDOWN_DATA}
        maxHeight={220}
        labelField="label"
        valueField="value"
        placeholder=""
        value={value}
        onChange={(item) => onChange(item.value as LeagueStandingsScope)}
        onFocus={measure}
        renderRightIcon={() => (
          <ChevronDown size={15} color={theme.gray[600]} strokeWidth={2.25} />
        )}
      />
    </View>
  )
}
